/**
 * Central Error Handler Service
 * Provides unified error processing, logging, and user notification
 */

import { v4 as uuidv4 } from 'uuid';
import { ErrorCategory, ErrorSeverity, ErrorCodes } from '../types/errors';
import type { 
  CyclingBuddyError, 
  ErrorAction,
  ErrorContext,
  ErrorCode
} from '../types/errors';

// Error handler configuration
interface ErrorHandlerConfig {
  enableDatabaseLogging: boolean;
  enableConsoleLogging: boolean;
  maxRetryAttempts: number;
  showToastNotifications: boolean;
  collectUserAgent: boolean;
  collectSessionData: boolean;
}

class ErrorHandlerService {
  private config: ErrorHandlerConfig;
  private sessionId: string;
  private userId?: string;
  private currentUrl?: string;
  private toastService?: any; // Will be injected

  constructor(config: Partial<ErrorHandlerConfig> = {}) {
    this.config = {
      enableDatabaseLogging: true,
      enableConsoleLogging: true,
      maxRetryAttempts: 3,
      showToastNotifications: true,
      collectUserAgent: true,
      collectSessionData: true,
      ...config
    };
    
    this.sessionId = uuidv4();
    this.updateCurrentUrl();
    
    // Listen for URL changes
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', () => this.updateCurrentUrl());
      
      // Track navigation in SPAs
      const originalPushState = history.pushState;
      history.pushState = (...args) => {
        originalPushState.apply(history, args);
        this.updateCurrentUrl();
      };
    }
  }

  /**
   * Set the toast service for user notifications
   */
  setToastService(toastService: any) {
    this.toastService = toastService;
  }

  /**
   * Set current user ID for error tracking
   */
  setUserId(userId: string | undefined) {
    this.userId = userId;
  }

  /**
   * Update current URL for error context
   */
  private updateCurrentUrl() {
    if (typeof window !== 'undefined') {
      this.currentUrl = window.location.href;
    }
  }

  /**
   * Create a structured error from various inputs
   */
  createError(
    category: ErrorCategory,
    message: string,
    options: {
      severity?: ErrorSeverity;
      code?: ErrorCode;
      technicalMessage?: string;
      context?: ErrorContext;
      originalError?: Error;
      actions?: ErrorAction[];
      retryable?: boolean;
    } = {}
  ): CyclingBuddyError {
    const error: CyclingBuddyError = {
      id: uuidv4(),
      category,
      severity: options.severity || this.determineSeverity(category),
      code: options.code,
      message,
      technicalMessage: options.technicalMessage,
      timestamp: new Date(),
      userId: this.userId,
      sessionId: this.sessionId,
      url: this.currentUrl,
      userAgent: this.config.collectUserAgent ? navigator?.userAgent : undefined,
      context: options.context,
      originalError: options.originalError,
      stackTrace: options.originalError?.stack,
      actions: options.actions || this.generateDefaultActions(category, options.retryable),
      retryable: options.retryable ?? this.isRetryable(category),
      retryCount: 0
    };

    return error;
  }

  /**
   * Handle an error with full processing pipeline
   */
  async handleError(error: CyclingBuddyError | Error | string, context?: ErrorContext): Promise<CyclingBuddyError> {
    let structuredError: CyclingBuddyError;

    // Convert to structured error if needed
    if (typeof error === 'string') {
      structuredError = this.createError(ErrorCategory.UNKNOWN, error, { context });
    } else if (error instanceof Error) {
      structuredError = this.createError(
        ErrorCategory.UNKNOWN,
        this.getUserFriendlyMessage(error.message),
        {
          technicalMessage: error.message,
          originalError: error,
          context
        }
      );
    } else {
      structuredError = error;
      if (context && !structuredError.context) {
        structuredError.context = context;
      }
    }

    // Log the error
    await this.logError(structuredError);

    // Show user notification
    this.notifyUser(structuredError);

    return structuredError;
  }

  /**
   * Handle API errors specifically
   */
  async handleApiError(
    error: any,
    apiEndpoint: string,
    operation: string,
    parameters?: Record<string, any>
  ): Promise<CyclingBuddyError> {
    const context: ErrorContext = {
      feature: 'api',
      operation,
      parameters,
      apiEndpoint,
      httpStatus: error.status || error.response?.status,
      responseData: error.response?.data || error.data
    };

    let category: ErrorCategory = ErrorCategory.API;
    let code: ErrorCode | undefined;
    let message = 'An error occurred while processing your request.';
    let severity: ErrorSeverity = ErrorSeverity.MEDIUM;

    // Categorize based on HTTP status
    if (context.httpStatus) {
      switch (context.httpStatus) {
        case 401:
          category = ErrorCategory.AUTHENTICATION;
          code = ErrorCodes.API_UNAUTHORIZED;
          message = 'Your session has expired. Please log in again.';
          severity = ErrorSeverity.HIGH;
          break;
        case 403:
          code = ErrorCodes.API_FORBIDDEN;
          message = 'You don\'t have permission to perform this action.';
          severity = ErrorSeverity.HIGH;
          break;
        case 404:
          code = ErrorCodes.API_NOT_FOUND;
          message = 'The requested resource was not found.';
          break;
        case 429:
          code = ErrorCodes.API_RATE_LIMIT;
          message = 'Too many requests. Please try again in a moment.';
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          code = ErrorCodes.API_SERVER_ERROR;
          message = 'Server is temporarily unavailable. Please try again later.';
          severity = ErrorSeverity.HIGH;
          break;
        default:
          message = 'An unexpected error occurred. Please try again.';
      }
    }

    // Check for network errors
    if (error.name === 'NetworkError' || error.message?.includes('Failed to fetch') || 
        (error instanceof TypeError && error.message?.includes('fetch')) ||
        context.parameters?.service === 'geocoding' && context.parameters?.customMessage) {
      category = ErrorCategory.NETWORK;
      code = ErrorCodes.NETWORK_UNAVAILABLE;
      // Use custom message if provided for specific services
      if (context.parameters?.customMessage) {
        message = context.parameters.customMessage;
      } else {
        message = 'Network connection failed. Please check your internet connection.';
      }
      severity = ErrorSeverity.HIGH;
    }

    const structuredError = this.createError(category, message, {
      severity,
      code,
      technicalMessage: error.message,
      context,
      originalError: error,
      retryable: this.isRetryableHttpStatus(context.httpStatus)
    });

    return this.handleError(structuredError);
  }

  /**
   * Handle routing-specific errors
   */
  async handleRoutingError(
    error: any,
    operation: string,
    routeData?: any
  ): Promise<CyclingBuddyError> {
    const context: ErrorContext = {
      feature: 'routing',
      operation,
      parameters: routeData
    };

    let code: ErrorCode;
    let message: string;
    let severity: ErrorSeverity = ErrorSeverity.MEDIUM;

    // Check if error already has a user-friendly message (from routeCalculation.ts)
    const errorMessage = error.message || error.toString();
    const isEnhancedError = errorMessage.includes('starting location is not accessible') || 
                           errorMessage.includes('destination is not accessible') ||
                           errorMessage.includes('waypoint') && errorMessage.includes('not accessible') ||
                           errorMessage.includes('route could be found') || 
                           errorMessage.includes('Invalid waypoints') ||
                           errorMessage.includes('service is temporarily unavailable') ||
                           errorMessage.includes('calculation timed out') ||
                           errorMessage.includes('failed due to invalid data');

    if (isEnhancedError) {
      // Use the enhanced message from routeCalculation.ts
      message = errorMessage;
      
      // Determine code and severity based on message content
      if (errorMessage.includes('starting location is not accessible')) {
        code = ErrorCodes.ROUTE_START_NOT_MAPPED;
      } else if (errorMessage.includes('destination is not accessible')) {
        code = ErrorCodes.ROUTE_END_NOT_MAPPED;
      } else if (errorMessage.includes('waypoint') && errorMessage.includes('not accessible')) {
        code = ErrorCodes.ROUTE_WAYPOINT_NOT_MAPPED;
      } else if (errorMessage.includes('route could be found')) {
        code = ErrorCodes.ROUTE_NO_PATH;
      } else if (errorMessage.includes('Invalid waypoints')) {
        code = ErrorCodes.ROUTE_INVALID_WAYPOINTS;
      } else if (errorMessage.includes('temporarily unavailable')) {
        code = ErrorCodes.ROUTE_CALCULATION_FAILED;
        severity = ErrorSeverity.HIGH;
      } else if (errorMessage.includes('timed out') || errorMessage.includes('timeout limit')) {
        code = ErrorCodes.NETWORK_TIMEOUT;
        severity = ErrorSeverity.HIGH;
      } else {
        code = ErrorCodes.ROUTE_CALCULATION_FAILED;
      }
    } else {
      // Fallback to analyzing error type for legacy errors
      if (error.message?.includes('No route found') || error.status === 404) {
        code = ErrorCodes.ROUTE_NO_PATH;
        message = 'No route could be found between these points. Try selecting different locations or using road access points.';
      } else if (error.message?.includes('Invalid waypoints')) {
        code = ErrorCodes.ROUTE_INVALID_WAYPOINTS;
        message = 'The selected locations are not valid for routing. Please choose accessible road locations.';
      } else if (error.message?.includes('Map data')) {
        code = ErrorCodes.ROUTE_MAP_DATA_MISSING;
        message = 'Map data is not available for this area. Try a different region.';
        severity = ErrorSeverity.HIGH;
      } else {
        code = ErrorCodes.ROUTE_CALCULATION_FAILED;
        message = 'Route calculation failed. Please try again or select different waypoints.';
      }
    }

    const structuredError = this.createError(ErrorCategory.ROUTING, message, {
      severity,
      code,
      technicalMessage: error.message,
      context,
      originalError: error,
      retryable: true
    });

    return this.handleError(structuredError);
  }

  /**
   * Log error to various destinations
   */
  private async logError(error: CyclingBuddyError): Promise<void> {
    // Console logging
    if (this.config.enableConsoleLogging) {
      console.group(`🚨 ${error.category.toUpperCase()} Error [${error.severity}]`);
      console.error('Message:', error.message);
      console.error('Code:', error.code);
      console.error('Technical:', error.technicalMessage);
      console.error('Context:', error.context);
      console.error('Stack:', error.stackTrace);
      console.groupEnd();
    }

    // Database logging
    if (this.config.enableDatabaseLogging) {
      try {
        await this.logToDatabase(error);
      } catch (logError) {
        console.error('Failed to log error to database:', logError);
      }
    }
  }

  /**
   * Log error to database
   */
  private async logToDatabase(error: CyclingBuddyError): Promise<void> {
    try {
      // Import the database service dynamically to avoid circular deps
      const { errorDatabaseService } = await import('./errorDatabase');
      
      // Check if database logging is available
      const isAvailable = await errorDatabaseService.isAvailable();
      if (!isAvailable) {
        console.warn('Error database logging is not available');
        return;
      }

      await errorDatabaseService.logError(error);
    } catch (logError) {
      console.error('Failed to log error to database:', logError);
    }
  }

  /**
   * Show user notification
   */
  private notifyUser(error: CyclingBuddyError): void {
    if (!this.config.showToastNotifications || !this.toastService) {
      return;
    }

    // Use existing toast service methods
    switch (error.category) {
      case ErrorCategory.NETWORK:
        // Check if this is a specific service failure
        if (error.context?.parameters?.service === 'geocoding') {
          this.toastService.showError(error.message, {
            title: 'Geocoding Service Unavailable',
            duration: 5000,
            persistent: true
          });
        } else if (error.context?.parameters?.service === 'storage') {
          this.toastService.showError(error.message, {
            title: 'Route Storage Unavailable',
            duration: 5000,
            persistent: true
          });
        } else if (error.context?.feature === 'calculate_route' || error.message.includes('BRouter')) {
          this.toastService.showError(error.message, {
            title: 'Routing Service Unavailable', 
            duration: 5000,
            persistent: true
          });
        } else {
          this.toastService.networkError();
        }
        break;
      case ErrorCategory.AUTHENTICATION:
        this.toastService.authError(error.message);
        break;
      case ErrorCategory.ROUTING:
        // Create a more specific title based on the error code
        let title = 'Calculation Failed';
        if (error.code === ErrorCodes.ROUTE_START_NOT_MAPPED) {
          title = 'Starting Point Issue';
        } else if (error.code === ErrorCodes.ROUTE_END_NOT_MAPPED) {
          title = 'Destination Issue';
        } else if (error.code === ErrorCodes.ROUTE_WAYPOINT_NOT_MAPPED) {
          title = 'Waypoint Issue';
        } else if (error.code === ErrorCodes.NETWORK_TIMEOUT) {
          title = 'Timeout Limit Reached';
        } else if (error.code === ErrorCodes.ROUTE_CALCULATION_FAILED) {
          title = 'Service Unavailable';
        }
        this.toastService.routeCalculationError(error.message, title);
        break;
      case ErrorCategory.STORAGE:
        // Storage-specific error handling
        let storageTitle = 'Storage Error';
        if (error.code === ErrorCodes.STORAGE_SAVE_FAILED) {
          storageTitle = 'Save Failed';
        } else if (error.code === ErrorCodes.STORAGE_LOAD_FAILED) {
          storageTitle = 'Load Failed';
        } else if (error.code === ErrorCodes.STORAGE_DELETE_FAILED) {
          storageTitle = 'Delete Failed';
        } else if (error.code === ErrorCodes.STORAGE_QUOTA_EXCEEDED) {
          storageTitle = 'Storage Full';
        }
        this.toastService.showError(error.message, {
          title: storageTitle,
          duration: 7000,
          persistent: error.severity === ErrorSeverity.HIGH
        });
        break;
      default:
        // Use the underlying toast service for generic errors (spread from ...toast)
        this.toastService.showError(error.message, {
          title: 'Error',
          duration: 5000
        });
    }
  }

  /**
   * Determine default severity based on category
   */
  private determineSeverity(category: ErrorCategory): ErrorSeverity {
    switch (category) {
      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.NETWORK:
        return ErrorSeverity.HIGH;
      case ErrorCategory.API:
      case ErrorCategory.ROUTING:
        return ErrorSeverity.MEDIUM;
      case ErrorCategory.VALIDATION:
      case ErrorCategory.UI:
        return ErrorSeverity.LOW;
      default:
        return ErrorSeverity.MEDIUM;
    }
  }

  /**
   * Generate default actions based on error type
   */
  private generateDefaultActions(category: ErrorCategory, retryable?: boolean): ErrorAction[] {
    const actions: ErrorAction[] = [];

    if (retryable) {
      actions.push({
        label: 'Try Again',
        type: 'retry'
      });
    }

    if (category === ErrorCategory.AUTHENTICATION) {
      actions.push({
        label: 'Log In',
        type: 'redirect',
        url: '/auth/login'
      });
    }

    if (category === ErrorCategory.NETWORK) {
      actions.push({
        label: 'Refresh Page',
        type: 'refresh'
      });
    }

    return actions;
  }

  /**
   * Check if error category is generally retryable
   */
  private isRetryable(category: ErrorCategory): boolean {
    switch (category) {
      case ErrorCategory.NETWORK:
      case ErrorCategory.API:
      case ErrorCategory.ROUTING:
        return true;
      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.VALIDATION:
        return false;
      default:
        return false;
    }
  }

  /**
   * Check if HTTP status is retryable
   */
  private isRetryableHttpStatus(status?: number): boolean {
    if (!status) return true;
    
    // Retryable status codes
    return [408, 429, 500, 502, 503, 504].includes(status);
  }

  /**
   * Convert technical error messages to user-friendly ones
   */
  private getUserFriendlyMessage(technicalMessage: string): string {
    // Map common technical errors to user-friendly messages
    const messageMap: Record<string, string> = {
      'fetch failed': 'Network connection failed. Please check your internet connection.',
      'timeout': 'The request took too long. Please try again.',
      'unauthorized': 'Your session has expired. Please log in again.',
      'forbidden': 'You don\'t have permission to perform this action.',
      'not found': 'The requested resource was not found.',
      'internal server error': 'Server error occurred. Please try again later.',
      'bad gateway': 'Service temporarily unavailable. Please try again later.',
      'service unavailable': 'Service temporarily unavailable. Please try again later.',
      'gateway timeout': 'Service temporarily unavailable. Please try again later.'
    };

    const lowerMessage = technicalMessage.toLowerCase();
    
    for (const [technical, friendly] of Object.entries(messageMap)) {
      if (lowerMessage.includes(technical)) {
        return friendly;
      }
    }

    return 'An unexpected error occurred. Please try again.';
  }
}

// Create singleton instance
export const errorHandler = new ErrorHandlerService();

// Export convenience methods
export const handleError = (error: CyclingBuddyError | Error | string, context?: ErrorContext) => 
  errorHandler.handleError(error, context);

export const handleApiError = (error: any, endpoint: string, operation: string, params?: Record<string, any>) =>
  errorHandler.handleApiError(error, endpoint, operation, params);

export const handleRoutingError = (error: any, operation: string, routeData?: any) =>
  errorHandler.handleRoutingError(error, operation, routeData);

export const createError = (category: ErrorCategory, message: string, options?: any) =>
  errorHandler.createError(category, message, options);

export default errorHandler;
